import React, { useEffect, useState } from 'react';
import { summaryApi } from "../../common/index";
import { toast } from "react-toastify";
import moment from "moment";
import { Edit, Search, User as UserIcon, Trash2, ChevronRight } from "lucide-react";
import { useTheme } from '../../components/Theme/ThemeProvider';
import ChangeUserRole from './ChangeUserRole';
import { authFetch } from '../../helpers/authFetch';

function AllUsers() {
    const { colors } = useTheme();
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [openUpdateRole, setOpenUpdateRole] = useState(false);
    const [loading, setLoading] = useState(false);
    const [updateUserDetails, setUpdateUserDetails] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        userId: ''
    });

    const fetchAllUsers = async () => {
        setLoading(true);
        try {
            const response = await authFetch(summaryApi.allUser.url, {
                method: summaryApi.allUser.method,
                
            });

            const responseData = await response.json();

            if (responseData.success) {
                setAllUsers(responseData.data);
                setFilteredUsers(responseData.data);
                toast.success(responseData.message);
            }

            if (responseData.error) {
                toast.error(responseData.message);
            }
        } catch (error) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllUsers();
    }, []);

    useEffect(() => {
        const filtered = allUsers.filter(user => 
            user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.role?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredUsers(filtered);
    }, [searchTerm, allUsers]);

    const getRoleColor = (role) => {
        switch(role) {
            case 'SUPERADMIN':
                return { bg: `${colors.primary}20`, text: colors.primary, border: colors.primary };
            case 'ADMIN':
                return { bg: `${colors.accent}20`, text: colors.accent, border: colors.accent };
            default:
                return { bg: `${colors.textSecondary}20`, text: colors.textSecondary, border: colors.textSecondary };
        }
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Header */}
            <div className="mb-8">
                <h1 
                    className="text-4xl font-bold mb-2"
                    style={{ color: colors.text }}
                >
                    All Users
                </h1>
                <p 
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                >
                    Manage all registered users ({filteredUsers.length})
                </p>
            </div>

            {/* Search Bar */}
            <div className="relative group">
                <Search 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-all"
                    style={{ color: colors.textSecondary }}
                />
                <input
                    type="text"
                    placeholder="Search users by name, email, or role..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-300 group-hover:shadow-lg"
                    style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`,
                        boxShadow: searchTerm ? `0 0 0 3px ${colors.primary}20` : 'none'
                    }}
                />
            </div>

            {/* Users Table */}
            <div 
                className="rounded-xl overflow-hidden shadow-lg transition-all hover:shadow-2xl"
                style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                }}
            >
                {loading ? (
                    <div className="flex items-center justify-center h-96">
                        <div 
                            className="animate-spin rounded-full h-12 w-12 border-4 border-transparent"
                            style={{
                                borderTopColor: colors.primary,
                                borderRightColor: `${colors.primary}40`,
                            }}
                        />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div 
                        className="flex flex-col items-center justify-center py-12"
                        style={{ backgroundColor: colors.bg }}
                    >
                        <UserIcon size={48} style={{ color: colors.textSecondary, opacity: 0.5 }} />
                        <p style={{ color: colors.textSecondary }} className="mt-4">
                            No users found
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead 
                                style={{ backgroundColor: colors.bg, borderBottom: `1px solid ${colors.border}` }}
                            >
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        S/N
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        User
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        Email
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        Role
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        Joined
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user, index) => (
                                    <tr
                                        key={user._id}
                                        className="transition-all duration-300 hover:scale-102 hover:shadow-md group"
                                        style={{
                                            backgroundColor: index % 2 === 0 ? colors.bg : colors.bgSecondary,
                                            borderBottom: `1px solid ${colors.border}`,
                                        }}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: colors.textSecondary }}>
                                            {index + 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="relative">
                                                    {user?.profilePic ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_BACKEND_URL}/${user.profilePic.replace(/\\/g, '/')}`}
                                                            alt="Profile"
                                                            className="w-10 h-10 rounded-full object-cover transition-transform hover:scale-110"
                                                            style={{ border: `2px solid ${colors.primary}` }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                                            style={{ backgroundColor: colors.primary }}
                                                        >
                                                            {user?.firstName?.charAt(0)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="font-medium" style={{ color: colors.text }}>
                                                        {user?.firstName} {user?.lastName}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: colors.textSecondary }}>
                                            {user?.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span 
                                                className="px-3 py-1 text-xs font-semibold rounded-full transition-all hover:scale-110"
                                                style={{
                                                    backgroundColor: getRoleColor(user?.role).bg,
                                                    color: getRoleColor(user?.role).text,
                                                    border: `1px solid ${getRoleColor(user?.role).border}`
                                                }}
                                            >
                                                {user?.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: colors.textSecondary }}>
                                            {moment(user?.createdAt).format('MMM DD, YYYY')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button
                                                onClick={() => {
                                                    setUpdateUserDetails(user);
                                                    setOpenUpdateRole(true);
                                                }}
                                                className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-lg group/btn"
                                                style={{
                                                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                                                }}
                                            >
                                                <Edit className="w-4 h-4 transition-transform group-hover/btn:rotate-12" />
                                                <span>Edit</span>
                                                <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {openUpdateRole && (
                <ChangeUserRole
                    onClose={() => setOpenUpdateRole(false)}
                    firstName={updateUserDetails.firstName}
                    lastName={updateUserDetails.lastName}
                    email={updateUserDetails.email}
                    role={updateUserDetails.role}
                    userId={updateUserDetails._id}
                    fetchAllUsers={fetchAllUsers}
                />
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
                
                @media (hover: hover) {
                    tr:hover {
                        transform: scale(1.02) translateY(-2px) !important;
                    }
                }
            `}</style>
        </div>
    );
}

export default AllUsers;